import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, XCircle, Activity, Frown, Users, HelpCircle, Lightbulb, Info } from 'lucide-react';
import { leadsAPI } from '../../../api/apiHelper';

export default function DealHealth() {
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState("Risk Score");
    const [healthData, setHealthData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHealth = async () => {
            if (!id || id === 'view') {
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const response = await leadsAPI.getDealHealth(id);
                if (response && response.data) {
                    setHealthData(response.data);
                } else {
                    setHealthData(null);
                }
            } catch (err) {
                console.error("Failed to fetch deal health:", err);
                setError("Unable to load deal health data.");
            } finally {
                setLoading(false);
            }
        };

        fetchHealth();
    }, [id]);

    const tabs = [
        "Risk Score",
        "MEDDICC Status",
        "Pain Points",
        "Competitor mentions",
        "Lost Deal Reasons"
    ];

    const renderEmptyState = (message) => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            color: '#9ca3af',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px dashed #e5e7eb',
            flex: 1
        } }>
            <Info size={ 24 } style={ { marginBottom: '12px', opacity: 0.5 } } />
            <div style={ { fontSize: '14px' } }>{ message }</div>
        </div>
    );

    const renderLoadingState = () => (
        <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280', padding: '40px' } }>
            Loading health data...
        </div>
    );

    const renderErrorState = () => (
        <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ef4444', padding: '40px' } }>
            <AlertTriangle size={ 20 } style={ { marginRight: '8px' } } />
            { error }
        </div>
    );

    const renderContent = () => {
        if (loading) return renderLoadingState();
        if (error) return renderErrorState();
        if (!healthData) return renderEmptyState("No input");

        switch (activeTab) {
            case "Risk Score":
                const health = healthData.dealHealth;
                if (!health) return renderEmptyState("No input");

                const riskValue = health.riskScore;
                const riskLevel = health.riskLevel;

                const isRiskNull = riskValue === null || riskValue === undefined;
                const isLevelNull = riskLevel === null || riskLevel === undefined;

                const scoreForColor = isRiskNull ? 0 : Number(riskValue);
                const riskColor = isRiskNull ? "#6b7280" : (scoreForColor < 40 ? "#22c55e" : scoreForColor < 70 ? "#eab308" : "#ef4444");

                return (
                    <div style={ { display: 'flex', flexDirection: 'column', gap: '20px' } }>
                        <div style={ { display: 'flex', alignItems: 'center', gap: '16px' } }>
                            <div style={ { position: 'relative', width: '80px', height: '80px', flexShrink: 0 } }>
                                <svg width="80" height="80" viewBox="0 0 36 36">
                                    <path
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                        fill="none"
                                        stroke="#e5e7eb"
                                        strokeWidth="3"
                                    />
                                    <path
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                        fill="none"
                                        stroke={ isRiskNull ? "#e5e7eb" : riskColor }
                                        strokeWidth="3"
                                        strokeDasharray={ `${isRiskNull ? 0 : riskValue}, 100` }
                                    />
                                </svg>
                                <div style={ { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontWeight: 'bold', fontSize: isRiskNull ? '10px' : '14px', color: isRiskNull ? '#9ca3af' : '#111827', textAlign: 'center', width: '90%', fontStyle: isRiskNull ? 'italic' : 'normal' } }>
                                    { isRiskNull ? "No input" : riskValue }
                                </div>
                            </div>
                            <div>
                                <div style={ { fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' } }>Risk Level</div>
                                <div style={ { fontSize: '18px', fontWeight: 700, color: riskColor, textTransform: 'capitalize', fontStyle: isLevelNull ? 'italic' : 'normal' } }>
                                    { isLevelNull ? "No input" : riskLevel }
                                </div>
                            </div>
                        </div>

                        { health.reason && (
                            <div style={ { background: '#f9fafb', borderRadius: '8px', padding: '16px' } }>
                                <div style={ { fontSize: '12px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' } }>Reasoning</div>
                                <div style={ { fontSize: '14px', color: '#374151', lineHeight: '1.6' } }>
                                    { health.reason }
                                </div>
                            </div>
                        ) }

                        { health.winSuggestions && health.winSuggestions.length > 0 && (
                            <div style={ { background: '#ecfdf5', borderRadius: '8px', padding: '16px', borderLeft: '4px solid #10b981' } }>
                                <div style={ { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' } }>
                                    <Lightbulb size={ 16 } color="#059669" />
                                    <div style={ { fontSize: '12px', color: '#059669', fontWeight: 700, textTransform: 'uppercase' } }>Win Suggestions</div>
                                </div>
                                <div style={ { fontSize: '14px', color: '#065f46', lineHeight: '1.6' } }>
                                    { Array.isArray(health.winSuggestions) ? health.winSuggestions.map((s, i) => {
                                        const text = typeof s === 'string' ? s : (s.text || JSON.stringify(s));
                                        return <div key={ i } style={ { marginBottom: i < health.winSuggestions.length - 1 ? '8px' : '0' } }>{ text }</div>;
                                    }) : (typeof health.winSuggestions === 'string' ? health.winSuggestions : JSON.stringify(health.winSuggestions)) }
                                </div>
                            </div>
                        ) }
                    </div>
                );

            case "MEDDICC Status":
                const meddic = healthData.meddic;
                if (!meddic || Object.keys(meddic).length === 0) return renderEmptyState("No input");

                const meddicKeys = ['metrics', 'economic_buyer', 'decision_criteria', 'decision_process', 'identified_pain', 'champion'];

                return (
                    <div style={ { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' } }>
                        { meddicKeys.map(key => {
                            const item = meddic[key];
                            if (!item) return null;
                            const label = key.replace(/_/g, ' ');

                            return (
                                <div key={ key } style={ { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', display: 'flex', gap: '12px' } }>
                                    <div style={ { flexShrink: 0 } }>
                                        { item.present ?
                                            <CheckCircle size={ 20 } color="#22c55e" fill="#dcfce7" /> :
                                            <XCircle size={ 20 } color="#ef4444" fill="#fee2e2" />
                                        }
                                    </div>
                                    <div>
                                        <div style={ { fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '4px', textTransform: 'uppercase' } }>
                                            { label }
                                        </div>
                                        <div style={ { fontSize: '13px', color: '#4b5563', lineHeight: '1.5' } }>
                                            { item.value || "Not identified" }
                                        </div>
                                    </div>
                                </div>
                            );
                        }) }
                        { meddic.reasoning && (
                            <div style={ { gridColumn: '1 / -1', background: '#f9fafb', padding: '16px', borderRadius: '8px', marginTop: '8px' } }>
                                <div style={ { fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' } }>Analysis</div>
                                <div style={ { fontSize: '14px', color: '#374151' } }>{ meddic.reasoning }</div>
                            </div>
                        ) }
                    </div>
                );

            case "Pain Points":
                const pains = healthData.painPoints;
                if (!pains || pains.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { display: 'flex', flexDirection: 'column', gap: '8px' } }>
                        { pains.map((point, idx) => (
                            <div key={ idx } style={ { display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '12px', background: '#fff', borderBottom: '1px solid #f3f4f6' } }>
                                <div style={ { width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', marginTop: '7px', flexShrink: 0 } }></div>
                                <div style={ { fontSize: '14px', color: '#111827', lineHeight: '1.5' } }>
                                    { typeof point === 'string' ? point : point.text || JSON.stringify(point) }
                                </div>
                            </div>
                        )) }
                    </div>
                );

            case "Competitor mentions":
                const competitors = healthData.competitors;
                if (!competitors || competitors.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { display: 'flex', flexDirection: 'column', gap: '16px' } }>
                        { competitors.map((comp, idx) => {
                            let competitorName = "Unknown Competitor";
                            if (typeof comp === 'string') {
                                competitorName = comp;
                            } else if (typeof comp === 'object' && comp !== null) {
                                competitorName = comp.competitor_name || comp.name || JSON.stringify(comp);
                            }
                            
                            return (
                                <div key={ idx } style={ { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' } }>
                                    <div style={ { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' } }>
                                        <Activity size={ 16 } color="#4b5563" />
                                        <div style={ { fontWeight: 700, color: '#111827', fontSize: '14px' } }>
                                            { competitorName }
                                        </div>
                                    </div>
                                    { typeof comp === 'object' && comp !== null && comp.details && (
                                        <div style={ { fontSize: '13px', color: '#4b5563', lineHeight: '1.6' } }>
                                            { comp.details }
                                        </div>
                                    ) }
                                </div>
                            );
                        }) }
                    </div>
                );

            case "Lost Deal Reasons":
                const lostResponse = healthData.lostDealResponse;
                if (!lostResponse) return renderEmptyState("No input");

                let status = "Analysis";
                let details = lostResponse;

                if (typeof lostResponse === 'string') {
                    const parts = lostResponse.split('\n');
                    if (parts[0].startsWith('Status:')) {
                        status = parts[0];
                        details = parts.slice(1).join('\n').trim();
                    }
                } else if (typeof lostResponse === 'object') {
                    status = lostResponse.status || "Analysis";
                    details = lostResponse.details || JSON.stringify(lostResponse);
                }

                return (
                    <div style={ { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '24px', textAlign: 'center' } }>
                        <div style={ { width: '48px', height: '48px', borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' } }>
                            <HelpCircle size={ 24 } color="#15803d" />
                        </div>
                        <div style={ { fontSize: '16px', fontWeight: 700, color: '#166534', marginBottom: '12px' } }>
                            { status }
                        </div>
                        <div style={ { fontSize: '14px', color: '#14532d', lineHeight: '1.6', maxWidth: '600px', margin: '0 auto', whiteSpace: 'pre-line' } }>
                            { details }
                        </div>
                    </div>
                );

            default:
                return <div>Select a tab</div>;
        }
    };

    return (
        <div style={ { background: '#fff', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }>

            {/* Internal Tabs Navigation - Always Visible */ }
            <div style={ { display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 16px', background: '#f9fafb' } }>
                { tabs.map(tab => (
                    <div
                        key={ tab }
                        onClick={ () => setActiveTab(tab) }
                        style={ {
                            padding: '12px 16px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: activeTab === tab ? '#2563eb' : '#6b7280',
                            borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                        } }
                    >
                        { tab }
                    </div>
                )) }
            </div>

            {/* Content Area - Shows Loader/Error/Content */ }
            <div style={ { flex: 1, padding: '16px', overflowY: 'auto' } }>
                { renderContent() }
            </div>

        </div>
    );
}
